import { Component, computed, effect, input, output, signal, untracked, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { OrganizationModel } from '../../../core/models/data/organization.model';
import { FormState, FormValidatorMessages } from '../../../core/enums/forms.enums';
import { CreatePropertyRequest, PropertyModel, UpdatePropertyRequest } from '../../../core/models/data/property.model';
import { PropertiesService } from '../../../core/services/property.service';
import { InputLabel } from "../../../parts/input-label/input-label.component";
import { UserSessionService } from '../../../core/services/user-session.service';
import { AddressVerificationModel, CreateAddressRequest, VerifiedAddressModel, VerifyAddressRequest } from '../../../core/models/data/address.model';
import { AddressFormValue } from '../../../core/models/ui/address-form-value.model';
import { AddressVerificationPrompt, AddressVerificationSide } from '../../../core/models/ui/address-verification-prompt.model';
import { AddressForm } from '../../../parts/address/address.form';
import { AddressUtilities } from '../../../core/utilities/address.utilities';
import { AddressDeliverability, AddressTypes } from '../../../core/enums/data.enums';
import { AddressPermission } from '../../../core/enums/permissions.enums';
import { FeatureFlag } from '../../../core/enums/feature-flags.enums';
import { HttpResponseCode } from '../../../core/enums/api.enums';
import { ServiceResultModel } from '../../../core/models/service/service-result.model';
import { ErrorMessages } from '../../../core/constants/constants';
import { ListConstants } from '../../../core/constants/lists.constants';
import { PropertiesMessagingConstants } from '../../../core/constants/messaging.constants';
import { AddressService } from '../../../core/services/address.service';
import { LocationService } from '../../../core/services/location.service';
import { AuthorizationService } from '../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../core/services/authorization/authorization-state.service';

@Component({
  selector: 'ms-property-form',
  imports: [InputLabel, ReactiveFormsModule, AddressForm],
  templateUrl: './property.form.html',
  styleUrl: './property.form.scss'
})
export class PropertyForm {
  @ViewChild(AddressForm) addressFormComponent?: AddressForm;

  public FormValidatorMessages = FormValidatorMessages;
  public FormState = FormState;

  public organization = input<OrganizationModel | undefined | null>(null);
  public property = input<PropertyModel | undefined>(undefined);
  public formState = input<FormState>(FormState.Create);

  public propertyForm: FormGroup;

  // A signal rather than a field: the submit button stays disabled across the verification call,
  // which can take the API's full 10-second provider timeout to come back.
  public isSubmitting = signal<boolean>(false);
  public noStreetAddress = signal<boolean>(false);

  // Only the create form defaults the country, so the explanation is scoped to it —
  // on edit the country shown is the Property's own.
  public countryTooltip = computed<string | undefined>(() =>
    this.formState() === FormState.Create ? PropertiesMessagingConstants.PROPERTIES_DEFAULT_COUNTRY_HINT() : undefined);

  public cancelRequested = output();
  public editConfirmationRequested = output();
  public submissionResult = output<ServiceResultModel>();

  /** The provider returned something worth asking about. The host shows the comparison dialog. */
  public addressVerificationRequested = output<AddressVerificationPrompt>();
  /** Verification could not run (outage, missing key, rate limit, or turned off for the organization). */
  public addressVerificationUnavailable = output();
  /** The provider rejected the address outright. Carries its message; the user has to edit the entry. */
  public addressVerificationRejected = output<string>();

  // The address as entered, held between the verify call and the user's answer. No verification
  // identifier travels with it: the API links a saved address to its verification by content.
  private pendingAddress: CreateAddressRequest | null = null;
  private pendingVerification?: AddressVerificationModel;

  constructor(
    private propertyService: PropertiesService,
    private addressService: AddressService,
    private locationService: LocationService,
    private authorizationService: AuthorizationService,
    private authorizationStateService: AuthorizationStateService,
    private fb: FormBuilder,
    private userSessionService: UserSessionService) {
    this.propertyForm = this.fb.group({
      title: ['', []],
      lotNumber: ['', []],
      address: [null, [Validators.required]]
    }, {});

    effect(() => {
      const state = this.formState();
      const property = this.property();

      untracked(() => {
        if (state === FormState.Update && property) {
          this.populateFormForEdit();
        } else if (state === FormState.Create) {
          this.resetForm();
        }
      });
    });
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  public async onSubmit(): Promise<void> {
    if (this.propertyForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    if (this.formState() === FormState.Update) {
      this.editConfirmationRequested.emit();
      return;
    }

    await this.beginSubmit();
  }

  //region Submit & address verification

  /**
   * The single entry point once the user has committed: the create form's Submit, and the edit
   * form's save confirmation. Verification runs here rather than in onSubmit so the edit
   * confirmation is answered before a paid call is made.
   */
  public async beginSubmit(): Promise<void> {
    const addressRequest = this.buildPhysicalAddressRequest();

    if (!addressRequest || !this.shouldVerifyAddress(addressRequest)) {
      await this.completeSubmit(addressRequest);
      return;
    }

    this.pendingAddress = addressRequest;
    this.pendingVerification = undefined;
    this.isSubmitting.set(true);

    const result = await this.addressService.verifyAddress(this.buildVerifyRequest(addressRequest));

    if (result.success && result.data) {
      await this.resolveVerification(result.data);
      return;
    }

    // 403 means verification is not available to this organization — the flag is off for them, or
    // the user lacks the permission. Feature flags are a deployment tool, so neither may be hinted
    // at: the property saves exactly as it would have had the client-side gate caught it first.
    if (result.statusCode === HttpResponseCode.FORBIDDEN) {
      await this.completeSubmit(addressRequest);
      return;
    }

    this.isSubmitting.set(false);
    this.reportVerificationFailure(result);
  }

  /** The host's answer to the comparison dialog: save the address the provider returned. */
  public async acceptVerifiedAddress(): Promise<void> {
    const standardized = this.pendingVerification?.standardizedAddress;
    await this.completeSubmit(standardized
      ? AddressUtilities.toCreateAddressRequest(standardized, AddressTypes.Physical)
      : this.pendingAddress);
  }

  /** The host's answer to the comparison dialog, or to the unavailable prompt: save what was entered. */
  public async keepEnteredAddress(): Promise<void> {
    await this.completeSubmit(this.pendingAddress);
  }

  /** The host's Edit / cancel: the entry stays as it is, and submitting again is a new verification. */
  public cancelVerification(): void {
    this.pendingAddress = null;
    this.pendingVerification = undefined;
    this.isSubmitting.set(false);
  }

  private async resolveVerification(verification: AddressVerificationModel): Promise<void> {
    this.pendingVerification = verification;

    // The skip rule is exact. Everything else gets a dialog, including DeliverableWithIssues on an
    // address the provider did not change — the two sides look identical but still need the warning.
    if (verification.deliverability === AddressDeliverability.Deliverable && !verification.isCorrected) {
      await this.completeSubmit(AddressUtilities.toCreateAddressRequest(verification.standardizedAddress, AddressTypes.Physical));
      return;
    }

    const prompt = await this.buildVerificationPrompt(verification);
    this.isSubmitting.set(false);
    this.addressVerificationRequested.emit(prompt);
  }

  private reportVerificationFailure(result: ServiceResultModel): void {
    // 422 is the provider refusing the input, usually a missing required field. Its message is more
    // specific than anything we could write, and saving unverified is not the right offer.
    if (result.statusCode === HttpResponseCode.UNPROCESSABLE_ENTITY) {
      this.pendingAddress = null;
      this.addressVerificationRejected.emit(result.message || ErrorMessages.DEFAULT_ERROR_MESSAGE());
      return;
    }

    // Everything left is an outage of a feature this organization does have — a provider failure, a
    // missing key, rate limiting. Naming it is safe here, and the address can still be saved.
    this.addressVerificationUnavailable.emit();
  }

  private async completeSubmit(addressRequest: CreateAddressRequest | null): Promise<void> {
    this.isSubmitting.set(true);

    try {
      if (this.formState() === FormState.Update) {
        await this.updateProperty(addressRequest);
      } else {
        await this.createProperty(addressRequest);
      }
    } finally {
      this.isSubmitting.set(false);
      this.pendingAddress = null;
      this.pendingVerification = undefined;
    }
  }

  private shouldVerifyAddress(addressRequest: CreateAddressRequest): boolean {
    if (!this.resolveOrganizationId()) {
      return false;
    }

    // Read against the active organization because that is the only organization whose flags the
    // session holds. Staff creating into another organization can therefore still reach the call;
    // the API answers 403 and that lands on the same "save unverified" path as an outage.
    if (!this.userSessionService.isFeatureEnabled(FeatureFlag.AddressVerification)) {
      return false;
    }

    const authState = this.authorizationStateService.authorizationState();
    if (!authState || !this.authorizationService.hasPermission(authState, AddressPermission.Verify, this.resolveOrganizationId())) {
      return false;
    }

    // On edit, only a changed address is worth a paid call. An unchanged one keeps the verification
    // it is already linked to.
    return this.formState() !== FormState.Update
      || !AddressUtilities.isSameAddressRequest(addressRequest, this.buildExistingPhysicalAddressRequest());
  }

  private buildVerifyRequest(addressRequest: CreateAddressRequest): VerifyAddressRequest {
    return {
      organizationId: this.resolveOrganizationId()!,
      primaryAddress: addressRequest.primaryAddress,
      secondaryAddress: addressRequest.secondaryAddress,
      city: addressRequest.city,
      stateProvinceId: addressRequest.stateProvinceId,
      primaryZip: addressRequest.primaryZip,
      secondaryZip: addressRequest.secondaryZip
    };
  }

  // The dialog shows state and country names, which the verification carries only as identifiers.
  // The entered side already has both models on the form; the standardized side borrows them unless
  // the provider moved the address to a different state, which is the only case worth a lookup.
  private async buildVerificationPrompt(verification: AddressVerificationModel): Promise<AddressVerificationPrompt> {
    const enteredValue = this.propertyForm.value.address as AddressFormValue | null;
    const original: AddressVerificationSide = {
      address: verification.originalAddress,
      stateProvince: enteredValue?.stateProvince,
      country: enteredValue?.country
    };

    const standardized = verification.standardizedAddress.stateProvinceId === verification.originalAddress.stateProvinceId
      ? { ...original, address: verification.standardizedAddress }
      : await this.lookupVerificationSide(verification.standardizedAddress);

    return { verification: verification, original: original, standardized: standardized };
  }

  private async lookupVerificationSide(address: VerifiedAddressModel): Promise<AddressVerificationSide> {
    const [states, countries] = await Promise.all([
      this.locationService.searchStateProvinces(
        { stateProvinceIds: [address.stateProvinceId] }, ListConstants.STATE_PROVINCES_ORDERBY_DEFAULT(), 0, 1),
      this.locationService.searchCountries(
        { countryIds: [address.countryId] }, ListConstants.COUNTRIES_ORDERBY_DEFAULT(), 0, 1)
    ]);

    return {
      address: address,
      stateProvince: states.data?.data?.[0],
      country: countries.data?.data?.[0]
    };
  }

  //endregion Submit & address verification

  private async createProperty(addressRequest: CreateAddressRequest | null): Promise<void> {
    let createProperty = {
      // The organization passed in, matching member.form's createMember — the caller decides which
      // organization it is creating in rather than this form assuming the active one (DEV-2643).
      organizationId: this.organization()?.organizationId,
      title: this.propertyForm.value.title?.trim() || null,
      lotNumber: this.propertyForm.value.lotNumber?.trim() || null
    } as CreatePropertyRequest;

    if (addressRequest) {
      createProperty.addresses = [addressRequest];
    }

    const submitResponse = await this.propertyService.createProperty(createProperty);

    if (submitResponse.success === true) {
      this.resetForm();
    }

    this.submissionResult.emit(submitResponse);
  }

  private async updateProperty(addressRequest: CreateAddressRequest | null): Promise<void> {
    const propertyData = this.property();
    if (!propertyData) {
      this.submissionResult.emit({ success: false, message: PropertiesMessagingConstants.PROPERTIES_EDIT_PROPERTY_ERROR() });
      return;
    }

    let updateRequest = {
      propertyId: propertyData.propertyId,
      title: this.propertyForm.value.title?.trim() || null,
      lotNumber: this.propertyForm.value.lotNumber?.trim() || null
    } as UpdatePropertyRequest;

    const existingNonPhysicalAddresses: CreateAddressRequest[] = (propertyData.addresses ?? [])
      .filter(a => a.addressType !== AddressTypes.Physical)
      .map(a => ({
        type: a.addressType,
        primaryAddress: a.primaryAddress,
        secondaryAddress: a.secondaryAddress ?? null,
        city: a.city,
        stateProvinceId: a.stateProvince.stateProvinceId,
        primaryZip: a.primaryZip,
        secondaryZip: a.secondaryZip ?? null,
      } as CreateAddressRequest));

    if (addressRequest) {
      updateRequest.addresses = [addressRequest, ...existingNonPhysicalAddresses];
    } else if (existingNonPhysicalAddresses.length > 0) {
      updateRequest.addresses = existingNonPhysicalAddresses;
    }

    const submitResponse = await this.propertyService.updateProperty(propertyData.propertyId, updateRequest);

    this.submissionResult.emit(submitResponse);
  }

  public isFieldRequired(field: string): boolean {
    const control = this.propertyForm.get(field);
    return control?.hasValidator(Validators.required) || false;
  }

  public toggleNoStreetAddress(): void {
    this.noStreetAddress.update(v => !v);
    this.setValidation();
  }

  public resetForm(): void {
    this.noStreetAddress.set(false);
    this.propertyForm.reset();
    this.addressFormComponent?.reset();
    this.setValidation();
    this.cancelVerification();

    if (this.formState() === FormState.Create) {
      void this.applyDefaultCountry();
    }
  }

  // A Property almost always sits in the same country as the Organization that owns it,
  // so the Organization's Physical address seeds the country on create. Nothing is
  // pre-selected when that address (or its country) is missing.
  private async applyDefaultCountry(): Promise<void> {
    const addresses = await this.userSessionService.getOrganizationAddresses();
    const defaultCountry = addresses?.find(a => a.addressType === AddressTypes.Physical)?.country;

    if (!defaultCountry) {
      return;
    }

    const addressControl = this.propertyForm.get('address');

    // The lookup is asynchronous, so re-check before writing: the form may have switched to
    // edit, or the user may have started entering an address of their own by now. Emptiness
    // is judged field by field rather than by the control holding a value at all, so a blank
    // value object left behind by a reset still counts as untouched.
    if (this.formState() !== FormState.Create
      || addressControl?.dirty
      || this.hasAddressInput(addressControl?.value as AddressFormValue | null)) {
      return;
    }

    addressControl?.setValue({
      country: defaultCountry,
      primaryAddress: '',
      secondaryAddress: '',
      city: '',
      stateProvince: undefined,
      zipPostal: ''
    } as AddressFormValue);
  }

  private markAllFieldsAsTouched(): void {
    this.propertyForm.markAllAsTouched();
    this.addressFormComponent?.markAllAsTouched();
  }

  public getFormState(): any {
    return {
      formValue: this.propertyForm.value,
      formValid: this.propertyForm.valid,
      formTouched: this.propertyForm.touched,
      formDirty: this.propertyForm.dirty,
    };
  }

  private resolveOrganizationId(): number | undefined {
    return this.organization()?.organizationId ?? this.property()?.organizationId;
  }

  private buildPhysicalAddressRequest(): CreateAddressRequest | null {
    if (this.noStreetAddress()) {
      return null;
    }

    const addressValue = this.propertyForm.value.address as AddressFormValue | null;
    if (!this.hasAddressInput(addressValue)) {
      return null;
    }

    return {
      type: AddressTypes.Physical,
      primaryAddress: addressValue.primaryAddress,
      secondaryAddress: (addressValue.secondaryAddress === "") ? null : addressValue.secondaryAddress,
      city: addressValue.city,
      stateProvinceId: addressValue.stateProvince!.stateProvinceId,
      primaryZip: AddressUtilities.getPrimaryZip(addressValue.zipPostal, addressValue.country?.alpha2Code),
      secondaryZip: AddressUtilities.getSecondaryZip(addressValue.zipPostal, addressValue.country?.alpha2Code),
    } as CreateAddressRequest;
  }

  // The Property's saved physical address in the shape a submit would post, so an edit that touched
  // only the title or lot number can be told apart from one that changed the address.
  private buildExistingPhysicalAddressRequest(): CreateAddressRequest | null {
    const existing = this.property()?.addresses?.find(a => a.addressType === AddressTypes.Physical);
    if (!existing) {
      return null;
    }

    return {
      type: AddressTypes.Physical,
      primaryAddress: existing.primaryAddress,
      secondaryAddress: existing.secondaryAddress ?? undefined,
      city: existing.city,
      stateProvinceId: existing.stateProvince.stateProvinceId,
      primaryZip: existing.primaryZip,
      secondaryZip: existing.secondaryZip ?? undefined
    };
  }

  // Tells a filled-in address apart from one that only holds blank fields, which is what a
  // reset leaves behind — that state must read as "nothing entered", not as user input.
  private hasAddressInput(address: AddressFormValue | null | undefined): address is AddressFormValue {
    return !!address && AddressUtilities.hasAddressInfo({
      country: address.country,
      primary: address.primaryAddress,
      secondary: address.secondaryAddress,
      city: address.city,
      stateProvince: address.stateProvince,
      primaryZip: address.zipPostal
    });
  }

  public setValidation(): void {
    const lotNumber = this.propertyForm?.get('lotNumber');
    const address = this.propertyForm?.get('address');

    if (this.noStreetAddress()) {
      lotNumber?.addValidators(Validators.required);
      address?.removeValidators(Validators.required);
    } else {
      lotNumber?.removeValidators(Validators.required);
      address?.addValidators(Validators.required);
    }

    lotNumber?.updateValueAndValidity({ emitEvent: false });
    address?.updateValueAndValidity({ emitEvent: false });
  }

  public populateFormForEdit(): void {
    const propertyData = this.property();
    if (!propertyData) return;

    this.propertyForm.patchValue({
      title: propertyData.title ?? '',
      lotNumber: propertyData.lotNumber ?? ''
    });

    const physicalAddress = propertyData.addresses?.find(a => a.addressType === AddressTypes.Physical);
    if (physicalAddress) {
      const addressFormValue: AddressFormValue = {
        country: physicalAddress.country,
        primaryAddress: physicalAddress.primaryAddress ?? '',
        secondaryAddress: physicalAddress.secondaryAddress ?? '',
        city: physicalAddress.city ?? '',
        stateProvince: physicalAddress.stateProvince,
        zipPostal: AddressUtilities.combineZip(physicalAddress.primaryZip, physicalAddress.secondaryZip)
      };

      this.noStreetAddress.set(false);
      this.propertyForm.patchValue({ address: addressFormValue });
    } else {
      this.noStreetAddress.set(true);
      this.propertyForm.patchValue({ address: null });
      this.addressFormComponent?.reset();
    }

    this.setValidation();
    this.propertyForm.markAsPristine();
  }


}
